import type {DiscoverGameResponse} from "libs/apiClient"
import { ContentCell } from "./ContentCell";
import {useId, useState} from "react";
import {GamePlayersTable} from "features/discover/GamePlayersTable";
import {Link} from "react-router";
import {GameAnalysisTable} from "features/discover/GameAnalysisTable";
import {DiscoverGameAnalysisDialog} from "features/discover/DiscoverGameAnalysisDialog";
import Modal from "../../../../../assets/js/components/Modal";
import client from "libs/client";
import { AllGenderOptions, ColorOptions } from "features/discover/types";
import {useToast} from "ui-kit";
import {ucFirst} from "features/discover/functions";
import { isGranted } from "pages/productManagement/functions";
import { Permission, Resource } from "pages/productManagement/types";

export function DiscoverGameDetail({
	gameData,
}: {
	gameData: DiscoverGameResponse;
}) {
	const { addSuccessToast, addErrorToast } = useToast();
	const contentProps = {
		id: gameData.id,
		mutation: client.patchDiscoverGame.useMutation(),
		invalidationKey: 'discoverGameDetail',
	};

	const canEditVip = isGranted(Resource.GAME_UPLOAD_SINGLE, Permission.GAME_VIP);

	const [hasAnalysisDialog, setHasAnalysisDialog] = useState(false);
	const [hasApprovalProgress, setHasApprovalProgress] = useState(false);

	const gameAnalysisApproveMutation = client.approveGameAnalysis.useMutation();
	const gameAnalysisDenyMutation = client.denyGameAnalysis.useMutation();

	const handleApproveGameAnalysis = () => {
		if (!confirm("Are you sure to approve game analysis?")) {
			return;
		}
		setHasApprovalProgress(true);
		gameAnalysisApproveMutation.mutate({
			id: gameData.id,
		}, {
			onSuccess: () => {
				setHasApprovalProgress(false)
				client.queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes("discoverGameDetail"), })
				addSuccessToast("Game analysis approved");
				setHasApprovalProgress(false);
			},
			onError: (error: unknown) => {
				addErrorToast(error instanceof Error ? error.message : 'Update failed');
				setHasApprovalProgress(false);
			}
		});
	}

	const handleDenyGameAnalysis = () => {
		if (!confirm("Are you sure to revoke game analysis approval?")) {
			return;
		}
		const reason = prompt("Fill in the reason (optional)") || null;

		setHasApprovalProgress(true);
		gameAnalysisDenyMutation.mutate({
			id: gameData.id,
			body: {
				reason: reason,
			},
		}, {
			onSuccess: () => {
				setHasApprovalProgress(false)
				client.queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes("discoverGameDetail"), })
				addSuccessToast("Game analysis denied");
				setHasApprovalProgress(false);
			},
			onError: (error: unknown) => {
				addErrorToast(error instanceof Error ? error.message : 'Update failed');
				setHasApprovalProgress(false);
			}
		});
	}

	const basicProps = [
		{
			title: "Date",
			type: "date",
			value: gameData.date,
		},
		{
			title: "Score",
			type: "string",
			value: "",
			formatter: () => `${gameData.home?.score || "-"} : ${gameData.away?.score || "-"}`
		},
		{
			title: "Type",
			type: "string",
			value: gameData.type || "-",
		},
		{
			title: "Game Page",
			type: "link",
			formatter: () => <Link to={`/game/${gameData.id}`} target="_blank"
								   className="button-link">{gameData.id}</Link>,
		},
		{
			title: "Description",
			type: "string",
			value: gameData.description || "-",
		},
		{
			title: "League",
			type: "string",
			value: gameData.league?.name || "-",
		},
		{
			title: "Age Category",
			type: "string",
			value: gameData.ageCategory || "-",
		},
		{
			title: "Is Discover",
			type: "boolean",
			value: gameData.isDiscover,
		},
		{
			title: "Is Vip",
			type: "boolean",
			...(canEditVip && { field: "isVip" }),
			value: gameData.isVip,
		},
		{
			title: "Is Hidden",
			type: "boolean",
			field: "isHidden",
			value: gameData.isHidden,
		},
		{
			title: "Is Hidden For Scout",
			type: "boolean",
			field: "isHiddenForScout",
			value: gameData.isHiddenForScout,
		},
		{
			title: "Starts On Left",
			type: "list",
			field: "homeTeamStartsOnLeft",
			value: gameData.homeTeamStartsOnLeft !== null ? (gameData.homeTeamStartsOnLeft ? "Home team" : "Away team") : '-',
			transformer: (value: string): boolean | null => {
				if (value === "Home team") {
					return true;
				}

				if (value === "Away team") {
					return false;
				}

				return null;
			},
			options: [
				{
					value: "-",
					label: "Not set",
				},
				{
					value: "Home team",
					label: "Home team",
				},
				{
					value: "Away team",
					label: "Away team",
				}
			]
		},
		{
			title: "Is Frozen",
			type: "boolean",
			value: gameData.isFrozen,
		},
		{
			title: "Owners",
			type: "string",
			formatter: () => <div>
				{gameData.owners.map((o, index) => <div key={`owner-${index}`}>{o.name}</div>)}
			</div>,
		},
		{
			title: "Gender",
			field: "gender",
			value: gameData.gender,
			type: "list",
			options: AllGenderOptions,
		},
	];

	const videoProps = [
		{
			title: "Mp4 Available At",
			type: "datetime",
			value: gameData.video?.mp4AvailableAt || null,
		},
		{
			title: "Stream Available At",
			type: "datetime",
			value: gameData.video?.mp4AvailableAt || null,
		},
	];

	const discoverProps = [
		{
			title: "Analysis Requested At",
			type: "datetime",
			value: gameData.analysisRequestedAt,
		},
		{
			title: "Requested Processing Level",
			type: "string",
			value: ucFirst(gameData.requestedProcessingLevel),
		},
		{
			title: `Dress kit ${gameData.gameDressKit ? `(${gameData.gameDressKit.name})` : ''}`,
			type: "string",
			value: "",
			formatter: () => {
				if (gameData.gameDressKit) {
					return (
						<>
							<span className="discover-dress-kit-dot" style={{
								borderColor: gameData.gameDressKit.shirtColorPrimary,
								...(gameData.gameDressKit.shirtColorSecondary ? { borderRightColor: gameData.gameDressKit.shirtColorSecondary } : {}),
							}}></span> Shirt
							<span className="discover-dress-kit-dot" style={{
								borderColor: gameData.gameDressKit.shortsColor,
							}}></span> Shorts
						</>
					)
				}

				return "Not set";
			},
		},
	];

	const analysisProps = [
		{
			title: "Provider Tag",
			type: "string",
			value: gameData.providerTag || "-",
		},
		{
			title: "Is Analysis Finished",
			type: "boolean",
			value: gameData.isAnalysisFinished,
		},
		...(gameData.isAnalysisFinished ? [{
			title: "Is Analysis Approved",
			type: "boolean",
			value: gameData.isAnalysisApproved,
			formatter: (value: boolean) => <div>
				<span className={value ? "true" : "false"}>
					{value ? 'Yes' : 'No'}
				</span>
				<span>
					{!value && gameData.analysisNotApprovedReason ? `, ${gameData.analysisNotApprovedReason}` : ""}
				</span>
			</div>
		}] : []),
		...(gameData.isAnalysisFinished && gameData.providerTag ? [{
			title: "Analysis approval",
			type: "link",
			formatter: () => <Link
				to={`#`}
				onClick={() => hasApprovalProgress ? undefined : (gameData.isAnalysisApproved ? handleDenyGameAnalysis() : handleApproveGameAnalysis())}
				className="button-link"
				aria-disabled={hasApprovalProgress}
			>{gameData.isAnalysisApproved ? `Revoke Approval` : `Approve`}</Link>,
		}] : []),
	];

	const homeTeamProps = [
		{
			title: "Home Game Club Name",
			type: "string",
			value: gameData.home.clubName,
		},
		{
			title: "Home Game Team Name",
			type: "string",
			value: gameData.home.teamName,
		},
		{
			title: "Home Club",
			type: "link",
			formatter: () => gameData.home?.club ?
				<Link to={`/discover/club/${gameData.home?.club.id}`} className="button-link">{gameData.home?.club.name}</Link> : "-",
		},
		{
			title: "Home Team",
			type: "link",
			formatter: () => gameData.home?.team ? <Link to={`/discover/team/${gameData.home?.team.id}`} className="button-link">{gameData.home?.team.name}</Link> : "-",
		},
		{
			title: "Home Team Shirt Color",
			type: "list",
			field: "homeColorShirt",
			value: gameData.home.colorShirt || "-",
			options: ColorOptions,
		},
		{
			title: "Home Team Short Color",
			type: "list",
			field: "homeColorShort",
			value: gameData.home.colorShort || "-",
			options: ColorOptions,
		},
	];

	const awayTeamProps = [
		{
			title: "Away Game Club Name",
			type: "string",
			value: gameData.away.clubName,
		},
		{
			title: "Away Game Team Name",
			type: "string",
			value: gameData.away.teamName,
		},
		{
			title: "Away Club",
			type: "link",
			formatter: () => gameData.away?.club ?
				<Link to={`/discover/club/${gameData.away?.club.id}`} className="button-link">{gameData.away?.club.name}</Link> : "-",
		},
		{
			title: "Away Team",
			type: "link",
			formatter: () => gameData.away?.team ? <Link to={`/discover/team/${gameData.away?.team.id}`} className="button-link">{gameData.away?.team.name}</Link> : "-",
		},
		{
			title: "Away Team Shirt Color",
			type: "list",
			field: "awayColorShirt",
			value: gameData.away.colorShirt || "-",
			options: ColorOptions,
		},
		{
			title: "Away Team Short Color",
			type: "list",
			field: "awayColorShort",
			value: gameData.away.colorShort || "-",
			options: ColorOptions,
		},
	];

	return (
		<>
			<div className="discover-card__title">Basic game data</div>
			<div className="discover-card">
				<div className="cell-grid">
					{basicProps.map((props) => {
						const key = useId();

						return <ContentCell key={key} {...contentProps} {...props} />
					})}
				</div>
			</div>

			<div className="discover-card__title">Video</div>
			<div className="discover-card">
				<div className="cell-grid">
					{videoProps.map((props) => {
						const key = useId();

						return <ContentCell key={key} {...contentProps} {...props} />
					})}
				</div>
			</div>

			{gameData.isDiscover && <>
				<div className="discover-card__title">Discover data</div>
				<div className="discover-card">
					<div className="cell-grid">
						{discoverProps.map((props) => {
							const key = useId();

							return <ContentCell key={key} {...contentProps} {...props} />
						})}
					</div>
				</div>
			</>}

			<div className="discover-card__title">
				<div>Game Analysis</div>
				<div>
					<span className="discover-link" onClick={() => setHasAnalysisDialog(true)}>manage</span>
				</div>
			</div>
			<div className="discover-card">
				<div className="cell-grid">
					{analysisProps.map((props) => {
						const key = useId();

						return <ContentCell key={key} {...contentProps} {...props} />
					})}
				</div>

				<GameAnalysisTable gameAnalysisData={gameData.gameAnalysis}/>
			</div>

			<div className="discover-columns">
				<div className="discover-column">
					<div className="discover-card__title">Home team</div>
					<div className="discover-card">
						<div className="cell-grid cell-grid--2">
							{homeTeamProps.map((props) => {
								const key = useId();

								return <ContentCell key={key} {...contentProps} {...props} />
							})}
						</div>
						<GamePlayersTable gamePlayersData={gameData.home.gamePlayers || []}/>
					</div>
				</div>
				<div className="discover-column">
					<div className="discover-card__title">Away team</div>
					<div className="discover-card">
						<div className="cell-grid cell-grid--2">
							{awayTeamProps.map((props) => {
								const key = useId();

								return <ContentCell key={key} {...contentProps} {...props} />
							})}
						</div>
						<GamePlayersTable gamePlayersData={gameData.away.gamePlayers || []}/>
					</div>
				</div>
			</div>

			<Modal title="Game Analysis" onClose={() => setHasAnalysisDialog(false)} show={hasAnalysisDialog} size="lg" position="top">
				<DiscoverGameAnalysisDialog gameData={gameData} invalidationQueryKey="discoverGameDetail" />
			</Modal>
		</>
	)
}
